import { Big } from 'hypo-shared-utils';

export const calculateAnnualAssetBasedFee = breakdown => {
  const initialValue = new Big(0);
  return breakdown
    .reduce((prev, i) => {
      if (i.feeAmount) {
        return prev.plus(i.feeAmount);
      }
      return prev;
    }, initialValue)
    .toNumber();
};

export const calculateTotalValue = feeScheduleDetails => {
  const initialValue = new Big(0);
  return feeScheduleDetails
    .reduce((prev, curr, index) => {
      if (curr.lowValue && index !== feeScheduleDetails.length - 1) {
        return prev.plus(curr.lowValue);
      }
      return prev;
    }, initialValue)
    .toNumber();
};

export const getDefaultBreakdown = () => {
  return [
    {
      id: 1,
      feeName: 'Advice Fee',
      feeAmount: 0,
      feeType: 0,
    },
    {
      id: 2,
      feeName: 'Platform Fee',
      feeAmount: 0,
      feeType: 0,
    },
    {
      id: 3,
      feeName: 'Strategy Fee',
      feeAmount: 0,
      feeType: 0,
    },
    {
      id: 4,
      feeName: 'Administrative Fee',
      feeAmount: 0,
      feeType: 0,
    },
  ];
};

export const getRowTemplateData = (
  { feeScheduleType, isFirst = false } = { feeScheduleType: 0, isFirst: false }
) => {
  const lowValue = isFirst && [1, 2].includes(feeScheduleType) ? 0 : null;
  return {
    lowValue,
    annualAssetBasedFee: 0,
    annualAssetBasedFeeType: 0,
    breakdown: getDefaultBreakdown(),
  };
};

export const getDefaultFeeScheduleDetails = ({ feeScheduleType = 0, defaultRows = 8 }) => {
  const feeScheduleDetails = [];
  for (let i = 0; i < defaultRows; i += 1) {
    const rowData = getRowTemplateData({
      feeScheduleType,
      isFirst: i === 0,
    });
    rowData.sequence = i + 1;
    feeScheduleDetails.push(rowData);
  }
  return feeScheduleDetails;
};

export const getDefaultNewFeeScheduleData = ({
  id,
  isMaxFee = false,
  allowMaxDiscount = false,
  maxDiscount = null,
  annualFeeNoLessThan = null,
  feeScheduleType,
  defaultRows = 8,
}) => {
  if (isMaxFee) {
    return {
      id,
      name: '',
      isMaxFee: true,
      maxFee: 0,
      isActived: false,
      allowMaxDiscount,
      maxDiscount,
      annualFeeNoLessThan,
      separateAccountManagerFee: true,
    };
  }
  const common = {
    id,
    name: '',
    minimumPortfolioSize: 0,
    feeScheduleType,
    inputFeeType: 0,
    isActived: false,
    allowMaxDiscount,
    maxDiscount,
  };
  const feeScheduleDetails = getDefaultFeeScheduleDetails({
    feeScheduleType: common.feeScheduleType,
    defaultRows,
  });
  return {
    ...common,
    feeScheduleDetails,
  };
};

export const isEmptyInput = value => {
  return value === null || typeof value === 'undefined' || String(value).trim() === '';
};

export const findPrevValidRowIndex = (rows, rowIndex) => {
  let prevIndex = rowIndex - 1;
  while (prevIndex > 0 && isEmptyInput(rows[prevIndex].lowValue)) {
    prevIndex -= 1;
  }
  return Math.max(prevIndex, 0);
};

export const findNextValidRowIndex = (rows, rowIndex) => {
  const maxRowIndex = rows.length - 1;
  let nextRowIndex = rowIndex + 1;
  while (nextRowIndex < maxRowIndex && isEmptyInput(rows[nextRowIndex].lowValue)) {
    nextRowIndex += 1;
  }
  return Math.min(nextRowIndex, maxRowIndex);
};

export const isFallInRange = (rows, rowIndex, value, amountFractionDigits) => {
  const prevIndex = findPrevValidRowIndex(rows, rowIndex);
  const nextIndex = findNextValidRowIndex(rows, rowIndex);
  const currentValue = new Big(value);
  const prevLowValue = rows[prevIndex].lowValue;
  const nextRowValue = rows[nextIndex].lowValue;
  const minPrecisionValue = 10 ** -amountFractionDigits;
  if (rowIndex === 0) {
    return true;
  }
  if (rowIndex === rows.length - 1 || isEmptyInput(nextRowValue)) {
    return currentValue.minus(minPrecisionValue).gte(prevLowValue);
  }
  return (
    currentValue.minus(minPrecisionValue).gte(prevLowValue) &&
    currentValue.plus(minPrecisionValue).lte(nextRowValue)
  );
};

export const isValidLowValue = (rows, rowIndex, value, amountFractionDigits) => {
  if (isEmptyInput(value)) {
    return false;
  }
  return isFallInRange(rows, rowIndex, value, amountFractionDigits);
};

export const autoCalculateHighValue = (rows, amountFractionDigits, needMinus = true) => {
  return rows.map((row, index, arr) => {
    let highValue = null;
    if (
      isValidLowValue(rows, index, row.lowValue, amountFractionDigits) &&
      index !== arr.length - 1
    ) {
      const nextValidRowIndex = findNextValidRowIndex(rows, index);
      const nextValidRowLowValue = rows[nextValidRowIndex].lowValue;
      const minPrecisionValue = needMinus ? 10 ** -amountFractionDigits : 0;
      if (
        !isEmptyInput(nextValidRowLowValue) &&
        Big(nextValidRowLowValue)
          .minus(minPrecisionValue)
          .gte(row.lowValue)
      ) {
        highValue = Big(nextValidRowLowValue)
          .minus(minPrecisionValue)
          .toNumber();
      }
    }
    return {
      ...row,
      highValue,
    };
  });
};

export const registerEventHandler = (el, type, listener, options) => {
  el.addEventListener(type, listener, options);
  return () => {
    el.removeEventListener(type, listener, options);
  };
};

export const isNullOrUndefined = value => {
  return value === undefined || value === null;
};

export const isHistoricManagerFee = historicManagerFee => {
  return (
    Array.isArray(historicManagerFee) &&
    historicManagerFee.length > 1 &&
    historicManagerFee.some(
      timeSchedule => timeSchedule?.managerFee !== historicManagerFee?.[0]?.managerFee
    )
  );
};

export const getHistoricMaxOrManagerFeeStartDate = (
  dateStr,
  formatDate,
  translate,
  isActualWorkflow
) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  if (isActualWorkflow && year === 1900) {
    return translate('historicalMaxFeeInception');
  }
  return formatDate(dateStr);
};
